/**
 * 官网内容后台接口类型，与 api-server `api/graphql/websitecontent/types` 对齐。
 * Long 标量（id、mediaId、sizeBytes）按字符串返回；时间字段为 ISO 8601 UTC 字符串。
 */

export type WebsiteContentStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'OFFLINE';
export type WebsiteContentType = 'NEWS' | 'BLOG' | 'VIDEO' | 'EVENT';
export type WebsiteContentLang = 'EN' | 'ES' | 'ZH';
export type WebsiteMediaType = 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type WebsiteContentAdminSort = 'UPDATED_AT' | 'PUBLISH_TIME';

export const WEBSITE_CONTENT_STATUSES: WebsiteContentStatus[] = ['DRAFT', 'SCHEDULED', 'PUBLISHED', 'OFFLINE'];
export const WEBSITE_CONTENT_TYPES: WebsiteContentType[] = ['NEWS', 'BLOG', 'VIDEO', 'EVENT'];
export const WEBSITE_CONTENT_LANGS: WebsiteContentLang[] = ['EN', 'ES', 'ZH'];

export interface WebsiteAdminContent {
    id: string;
    slug: string;
    /** 官网英文详情页地址；其他语言在路径前加 /es、/zh。未发布时官网打不开 */
    publicUrl: string;
    status: WebsiteContentStatus;
    hasUnpublishedChanges: boolean;
    /** 草稿哈希：保存、发布、定时时作为 baseDraftHash 带回，期间有人改过会报编辑冲突 */
    draftHash: string | null;
    displayDate: string | null;
    scheduledAt: string | null;
    scheduleFailureReason: string | null;
    firstPublishedAt: string | null;
    lastPublishedAt: string | null;
    updatedAt: string;
    /** 为 null 表示系统（定时发布） */
    updatedBy: string | null;
    updatedByName: string | null;
}

export interface WebsiteAdminListItem {
    content: WebsiteAdminContent;
    contentType: WebsiteContentType;
    title: string;
    coverThumbnailUrl: string | null;
}

export interface WebsiteAdminStatusCount {
    status: WebsiteContentStatus;
    count: number;
}

export interface WebsiteAdminList {
    items: WebsiteAdminListItem[];
    total: number;
    statusCounts: WebsiteAdminStatusCount[];
    /** 定时发布未成功、已退回草稿的篇数（不受筛选影响） */
    scheduleFailedCount: number;
}

export interface WebsiteImageSource {
    url: string;
    width: number;
}

export interface WebsiteMediaAsset {
    mediaId: string;
    mediaType: WebsiteMediaType;
    url: string;
    mimeType: string;
    width: number | null;
    height: number | null;
    srcset: WebsiteImageSource[];
}

export interface WebsiteAdminLocalization {
    lang: WebsiteContentLang;
    title: string;
    summary: string;
    bodyJson: string | null;
    /** 正文最后修改时间（只改标题摘要不算）；从未填写过正文为 null */
    bodyUpdatedAt: string | null;
}

export interface WebsiteAdminAttachment {
    mediaId: string;
    displayName: string;
    sortOrder: number;
    url: string | null;
    fileName: string | null;
    sizeBytes: string | null;
}

export interface WebsiteAdminDetail {
    content: WebsiteAdminContent;
    contentType: WebsiteContentType;
    coverMediaId: string | null;
    coverAlt: string | null;
    videoMediaId: string | null;
    videoDurationSeconds: number | null;
    posterMediaId: string | null;
    /** YYYY-MM-DD；为 null 表示自动 */
    manualDisplayDate: string | null;
    localizations: WebsiteAdminLocalization[];
    attachments: WebsiteAdminAttachment[];
    media: WebsiteMediaAsset[];
}

export interface WebsiteDeletedItem {
    id: string;
    slug: string;
    title: string;
    coverThumbnailUrl: string | null;
    /** 删除前的状态 */
    status: WebsiteContentStatus;
    deletedAt: string | null;
    deletedByName: string | null;
}

export interface WebsiteDeletedList {
    items: WebsiteDeletedItem[];
    total: number;
}

export interface WebsiteBatchSkip {
    contentId: string;
    /** 错误消息键 */
    reason: string;
    status: WebsiteContentStatus | null;
}

export interface WebsiteBatchResult {
    succeededIds: string[];
    skipped: WebsiteBatchSkip[];
}

export interface WebsitePreviewLink {
    url: string;
    expiresAt: string;
}

export interface WebsiteContentLocalizationInput {
    lang: WebsiteContentLang;
    title: string;
    summary: string;
    /** 为 null 表示清空 */
    bodyJson: string | null;
}

export interface WebsiteContentAttachmentInput {
    mediaId: string;
    displayName: string;
    sortOrder: number;
}

/**
 * 草稿入参：只放有改动的部分。字段不出现表示不变，显式为 null 表示清空；
 * localizations 只含有改动的语言，每种语言整份提交。
 */
export interface WebsiteContentDraftInput {
    contentType?: WebsiteContentType;
    manualDisplayDate?: string | null;
    coverMediaId?: string | null;
    coverAlt?: string | null;
    videoMediaId?: string | null;
    videoDurationSeconds?: number | null;
    posterMediaId?: string | null;
    localizations?: WebsiteContentLocalizationInput[];
    attachments?: WebsiteContentAttachmentInput[] | null;
}

export interface WebsiteMediaVariantInput {
    width: number;
    /** webp 或 jpeg */
    format: 'webp' | 'jpeg';
    sizeBytes: number;
}

export interface WebsiteMediaUploadInput {
    mediaType: WebsiteMediaType;
    fileName: string;
    sizeBytes: number;
    variants?: WebsiteMediaVariantInput[] | null;
}

export interface WebsiteMediaUploadTarget {
    objectKey: string;
    url: string;
    width: number | null;
    format: string | null;
}

export interface WebsiteMediaUploadTicket {
    mediaId: string;
    original: WebsiteMediaUploadTarget;
    variants: WebsiteMediaUploadTarget[];
    expiresAt: string;
}

export interface WebsiteMediaItem {
    id: string;
    mediaType: WebsiteMediaType;
    fileName: string;
    mimeType: string;
    sizeBytes: string;
    width: number | null;
    height: number | null;
    durationSeconds: number | null;
    url: string;
    thumbnailUrl: string | null;
    srcset: WebsiteImageSource[];
    referencedCount: number;
    createdAt: string;
}

export interface WebsiteMediaList {
    items: WebsiteMediaItem[];
    total: number;
}

export type WebsiteLogObjectType = 'CONTENT' | 'MEDIA';
export type WebsiteLogOperationType =
    'CREATE' | 'UPDATE' | 'PUBLISH' | 'SCHEDULE' | 'CANCEL_SCHEDULE' | 'UNPUBLISH' | 'DELETE' | 'UPLOAD';

export interface WebsiteOperationLog {
    id: string;
    operatedAt: string;
    /** 为 null 表示系统（定时发布） */
    operatorId: string | null;
    operatorName: string | null;
    operationType: WebsiteLogOperationType;
    objectType: WebsiteLogObjectType;
    objectId: string;
    objectName: string | null;
    slug: string | null;
    ip: string | null;
    scheduledBy: string | null;
    description: string | null;
}

export interface WebsiteOperationLogList {
    items: WebsiteOperationLog[];
    total: number;
}

export interface WebsiteMediaBatchResult {
    succeededIds: string[];
    skipped: { mediaId: string; reason: string; referencedCount: number | null }[];
}
